<template>
  <div class="page-wrapper bg-light min-vh-100">
    <div class="content container-fluid py-3 py-md-4" v-if="project">
      <div
        class="page-header bg-white shadow-sm rounded-lg p-3 p-md-4 mb-3 mb-md-4"
      >
        <div class="row">
          <div class="col-12 col-lg-7 mb-3 mb-lg-0">
            <div
              class="d-flex flex-column flex-md-row align-items-md-center mb-2"
            >
              <h3
                class="page-title font-weight-bold text-dark mb-2 mb-md-0 mr-md-3 h4 h3-md"
              >
                {{ project.tenCongTrinh }}
              </h3>
              <span
                class="badge badge-pill px-3 py-2 align-self-start align-self-md-center"
                :class="getStatusClass(project.trangThai)"
              >
                {{ getStatusText(project.trangThai) }}
              </span>
            </div>

            <div class="text-muted small mt-2">
              <div class="d-flex align-items-start mb-1">
                <i class="la la-map-marker text-danger mr-2 mt-1"></i>
                <span>{{ project.diaChi || "Chưa cập nhật địa chỉ" }}</span>
              </div>
              <div class="d-flex flex-wrap">
                <span class="mr-3"
                  ><i class="la la-user-tie text-primary mr-1"></i> PM:
                  <strong>{{ project.quanLy || "N/A" }}</strong></span
                >
                <span v-if="project.maCongTrinh"
                  ><i class="la la-barcode mr-1"></i>
                  {{ project.maCongTrinh }}</span
                >
              </div>
            </div>
          </div>

          <div class="col-12 col-lg-5 text-lg-right">
            <div
              class="d-flex flex-wrap justify-content-start justify-content-lg-end gap-2"
            >
              <button
                class="btn btn-light border text-dark font-weight-500 flex-grow-1 flex-lg-grow-0 mb-2 mb-lg-0 mr-2"
                @click="showCauHinhModal = true"
              >
                <i class="la la-sliders mr-1"></i> Cấu hình
              </button>

              <button
                v-if="!isThiCong"
                class="btn btn-success font-weight-bold flex-grow-1 flex-lg-grow-0 mb-2 mb-lg-0 mr-2"
                @click="batDauThiCong"
              >
                <i class="la la-play mr-1"></i> Bắt đầu
              </button>

              <button
                class="btn btn-primary flex-grow-1 flex-lg-grow-0 mb-2 mb-lg-0 mr-2"
                :disabled="!isThiCong"
                :title="isThiCong ? 'Xuất báo cáo' : 'Dự án chưa thi công'"
              >
                <i class="la la-download mr-1"></i>
                <span class="d-inline d-lg-none">Báo cáo</span
                ><span class="d-none d-lg-inline">Xuất báo cáo</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="kpi-scroll-wrapper mb-4">
        <div class="row flex-nowrap flex-lg-wrap kpi-row mx-0 mx-lg-n2">
          <div class="col-10 col-sm-6 col-lg-3 px-1 px-lg-2 mb-0 mb-lg-3">
            <div class="card border-0 shadow-sm h-100 stat-card">
              <div class="card-body p-3 d-flex align-items-center">
                <div class="avatar-circle bg-soft-primary text-primary mr-3">
                  <i class="la la-chart-pie font-size-24"></i>
                </div>
                <div>
                  <span
                    class="d-block text-muted small font-weight-bold text-uppercase"
                    >Tiến độ</span
                  >
                  <h4 class="mb-0 font-weight-bold text-dark">
                    {{ project.tienDo || 0 }}%
                  </h4>
                </div>
              </div>
              <div class="progress" style="height: 4px">
                <div
                  class="progress-bar bg-primary"
                  :style="{ width: (project.tienDo || 0) + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <div class="col-10 col-sm-6 col-lg-3 px-1 px-lg-2 mb-0 mb-lg-3">
            <div class="card border-0 shadow-sm h-100 stat-card">
              <div class="card-body p-3 d-flex align-items-center">
                <div class="avatar-circle bg-soft-danger text-danger mr-3">
                  <i class="la la-money font-size-24"></i>
                </div>
                <div>
                  <span
                    class="d-block text-muted small font-weight-bold text-uppercase"
                    >Ngân sách</span
                  >
                  <h4
                    class="mb-0 font-weight-bold text-dark text-truncate"
                    style="max-width: 150px"
                  >
                    {{ formatCurrency(project.nganSachDuKien) }}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          <div class="col-10 col-sm-6 col-lg-3 px-1 px-lg-2 mb-0 mb-lg-3">
            <div class="card border-0 shadow-sm h-100 stat-card">
              <div class="card-body p-3 d-flex align-items-center">
                <div class="avatar-circle bg-soft-warning text-warning mr-3">
                  <i class="la la-cubes font-size-24"></i>
                </div>
                <div>
                  <span
                    class="d-block text-muted small font-weight-bold text-uppercase"
                    >Đề xuất VT</span
                  >
                  <h4 class="mb-0 font-weight-bold text-dark">
                    {{ project.deXuatVatTu?.length || 0 }}
                    <small class="font-size-12 text-muted">phiếu</small>
                  </h4>
                </div>
              </div>
            </div>
          </div>

          <div class="col-10 col-sm-6 col-lg-3 px-1 px-lg-2 mb-0 mb-lg-3">
            <div class="card border-0 shadow-sm h-100 stat-card">
              <div class="card-body p-3 d-flex align-items-center">
                <div class="avatar-circle bg-soft-success text-success mr-3">
                  <i class="la la-users font-size-24"></i>
                </div>
                <div>
                  <span
                    class="d-block text-muted small font-weight-bold text-uppercase"
                    >Nhân sự</span
                  >
                  <h4 class="mb-0 font-weight-bold text-dark">
                    {{ project.nhanSuThamGia?.length || 0 }}
                    <small class="font-size-12 text-muted">người</small>
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card tab-card rounded-lg border-0" style="min-height: 600px">
        <div class="card-header bg-white border-bottom p-0 sticky-top-tabs">
          <ul class="nav nav-tabs-modern d-flex flex-nowrap overflow-auto">
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: currentTab === 'tong_quan' }"
                href="#"
                @click.prevent="setTab('tong_quan')"
                ><i class="la la-dashboard mr-1"></i> Tổng Quan</a
              >
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: currentTab === 'nhan_su' }"
                href="#"
                @click.prevent="setTab('nhan_su')"
                ><i class="la la-users mr-1"></i> Kế hoạch & Nhân sự</a
              >
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="[
                  { active: currentTab === 'vat_tu' },
                  { disabled: !isThiCong },
                ]"
                href="#"
                @click.prevent="setTab('vat_tu')"
                ><i class="la la-box mr-1"></i> Đề xuất & vật tư
                <i v-if="!isThiCong" class="la la-lock ml-1 text-muted"></i
              ></a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="[
                  { active: currentTab === 'nhat_ky' },
                  { disabled: !isThiCong },
                ]"
                href="#"
                @click.prevent="setTab('nhat_ky')"
                ><i class="la la-comments mr-1"></i> Báo cáo & Lịch sử tiến độ
                <i v-if="!isThiCong" class="la la-lock ml-1 text-muted"></i
              ></a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="[
                  { active: currentTab === 'trao_doi' },
                  { disabled: !isThiCong },
                ]"
                href="#"
                @click.prevent="setTab('trao_doi')"
                ><i class="la la-comments mr-1"></i> Trao đổi
                <i v-if="!isThiCong" class="la la-lock ml-1 text-muted"></i
              ></a>
            </li>
            <li class="nav-item">
              <a
                class="nav-link"
                :class="{ active: currentTab === 'tai_lieu' }"
                href="#"
                @click.prevent="setTab('tai_lieu')"
                ><i class="la la-file-text mr-1"></i> Tài liệu & Kiểm định</a
              >
            </li>
          </ul>
        </div>

        <div class="card-body pt-3 pl-0 pr-0 pb-0">
          <transition name="fade" mode="out-in">
            <keep-alive>
              <component :is="activeComponent" />
            </keep-alive>
          </transition>
        </div>
      </div>

      <div v-if="showCauHinhModal">
        <div
          class="modal fade show d-block"
          tabindex="-1"
          style="background: rgba(0, 0, 0, 0.5)"
        >
          <div
            class="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div class="modal-content" style="height: 90vh">
              <div class="modal-header">
                <h5 class="modal-title text-truncate">
                  Cấu hình: {{ project.tenCongTrinh }}
                </h5>
                <button
                  type="button"
                  class="close"
                  @click="showCauHinhModal = false"
                >
                  <span>&times;</span>
                </button>
              </div>
              <div class="modal-body p-0" style="overflow: hidden">
                <CauHinhCongTrinh />
              </div>
              <div class="modal-footer py-2">
                <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  @click="showCauHinhModal = false"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="d-flex flex-column justify-content-center align-items-center"
      style="height: 100vh"
    >
      <div class="spinner-border text-primary mb-3"></div>
      <h5 class="text-muted">Đang tải dữ liệu...</h5>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent, watch } from "vue";
import { useRoute } from "vue-router";
import { useCongTrinhStore } from "@/stores/congTrinhStore";
import { storeToRefs } from "pinia";

// Async Components
const TabTongQuan = defineAsyncComponent(() =>
  import("./components/TabTongQuan.vue")
);
const TabNhanSu = defineAsyncComponent(() =>
  import("./components/TabNhanSu.vue")
);
const TabVatTu = defineAsyncComponent(() =>
  import("./components/TabVatTu.vue")
);
const TabNhatKy = defineAsyncComponent(() =>
  import("./components/TabNhatKy.vue")
);
const TabTraoDoi = defineAsyncComponent(() =>
  import("./components/TabTraoDoi.vue")
);
const TabTaiLieu = defineAsyncComponent(() =>
  import("./components/TabTaiLieu.vue")
);
const CauHinhCongTrinh = defineAsyncComponent(() =>
  import("./components/CauHinhCongTrinh.vue")
);

const route = useRoute();
const store = useCongTrinhStore();
const { congTrinhDangChon: project } = storeToRefs(store);

const currentTab = ref("tong_quan");
const showCauHinhModal = ref(false);

// const isThiCong = computed(
//   () => project.value && project.value.trangThai === "DangThiCong"
// );
const isThiCong = computed(
  () => project.value && project.value.trangThai === "DANG_THI_CONG"
);

// UI Helpers
const getStatusClass = (st) =>
  ({
    MOI_TAO: "badge-soft-secondary text-secondary",
    DANG_THI_CONG: "badge-soft-success text-success",
    TAM_DUNG: "badge-soft-warning text-warning",
    HOAN_THANH: "badge-soft-primary text-primary",
  }[st] || "badge-light");

const getStatusText = (st) =>
  ({
    MOI_TAO: "Mới khởi tạo",
    DANG_THI_CONG: "Đang thi công",
    TAM_DUNG: "Tạm dừng",
    HOAN_THANH: "Hoàn thành",
  }[st] || st);

const formatCurrency = (val) =>
  new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  }).format(val || 0);

// Logic Tab
const setTab = (tab) => {
  if ((tab === "vat_tu" || tab === "nhat_ky") && !isThiCong.value) {
    alert("Dự án chưa bắt đầu thi công.");
    return;
  }
  currentTab.value = tab;
};

const activeComponent = computed(() => {
  const tabs = {
    tong_quan: TabTongQuan,
    nhan_su: TabNhanSu,
    vat_tu: TabVatTu,
    nhat_ky: TabNhatKy,
    trao_doi: TabTraoDoi,
    tai_lieu: TabTaiLieu,
  };
  return tabs[currentTab.value] || TabTongQuan;
});

const batDauThiCong = () => {
  if (!project.value || project.value.trangThai === "DANG_THI_CONG") return;
  if (confirm("Xác nhận bắt đầu thi công?")) store.batDauThiCong();
};
// === Sử lý chuyển tap lấy id không bị lỗi trắng dữ liệu ===
onMounted(() => {
  if (route.params.id) store.chonCongTrinh(route.params.id);
});
watch(
  () => route.params.id,
  (id) => {
    if (id) store.chonCongTrinh(id);
  }
);
</script>

<style scoped>
.tab-card {
  box-shadow: none;
  background-color: #ffffff00;
}
/* ========== MOBILE FIRST RESPONSIVE ========== */

/* Header Title Responsive */
.h4 {
  font-size: 1.25rem;
}
@media (min-width: 768px) {
  .h3-md {
    font-size: 1.75rem;
  }
}

/* KPI Cards Scrollable Row (Horizontal Scroll on Mobile) */
@media (max-width: 991px) {
  .kpi-scroll-wrapper {
    overflow-x: auto;
    padding-bottom: 10px; /* Space for scrollbar */
    margin-right: -15px; /* Offset container padding */
    padding-right: 15px;
  }
  .kpi-scroll-wrapper::-webkit-scrollbar {
    display: none;
  } /* Hide scrollbar look clean */
  .kpi-row {
    display: flex;
    flex-wrap: nowrap;
  }
  .stat-card {
    min-width: 160px; /* Minimum width for cards on mobile */
  }
}

/* Soft Colors */
.bg-soft-primary {
  background-color: #eff6ff;
}
.bg-soft-danger {
  background-color: #fef2f2;
}
.bg-soft-warning {
  background-color: #fffbeb;
}
.bg-soft-success {
  background-color: #ecfdf5;
}
.badge-soft-success {
  background-color: #d1fae5;
  color: #065f46;
}
.badge-soft-secondary {
  background-color: #f3f4f6;
  color: #374151;
}
.badge-soft-warning {
  background-color: #fef3c7;
  color: #92400e;
}
.badge-soft-primary {
  background-color: #dbeafe;
  color: #1e40af;
}

/* Card Hover */
.stat-card {
  transition: transform 0.2s;
}
.stat-card:hover {
  transform: translateY(-3px);
}
.avatar-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.font-size-24 {
  font-size: 24px;
}
.font-size-12 {
  font-size: 12px;
}

/* Modern Tabs */
.nav-tabs-modern {
  border-bottom: 2px solid #e9ecef;
  scrollbar-width: none;
}
.nav-tabs-modern::-webkit-scrollbar {
  display: none;
}
.nav-tabs-modern .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 600;
  padding: 1rem 1.2rem;
  white-space: nowrap;
}
.nav-tabs-modern .nav-link.active {
  color: #007bff;
  border-bottom-color: #007bff;
  background: transparent;
}
.nav-tabs-modern .nav-link.disabled {
  opacity: 0.5;
}

/* Modal Fix (Giữ nguyên yêu cầu của bạn) */
.modal-dialog-scrollable .modal-content {
  max-height: 90vh;
}
</style>
